import {DatePicker} from "../../../../../../components/fields/time/DatePicker";
import {
    UiComponentDataContext,
    UiComponentRendererPlugin,
    UiComponentRenderingContext,
    UiComponentRenderingProps,
    UiComponentStateContext,
    UiComponentValidationContext
} from "../UiComponentRendererPlugin";
import {ReactElement} from "react";
import {AttributeDefinitionDto} from "../../../../../../common/apis/stereotype/AttributeDefinitionDto";
import {ElementDto} from "../../../../../../common/apis/element/ElementDto";
import {UiComponent} from "../model/UiComponent";
import {t} from "../../../../../../store/localization/TranslMessasge";
import {DatePickerField} from "../model/DatePickerField";
import {ValuePersister} from "./ValuePersister";
import {PropertyValuePersister} from "./PropertyValuePersister";
import elementService from "../../../elementdetail/service/ElementService";
import ErrorFieldPlaceholder from "./ErrorFieldPlaceholder";
import {StereotypesTranslationKey} from "../../StereotypesTranslationKey";

export class DatePickerFieldPlugin implements UiComponentRendererPlugin {

    private readonly valuePersister: ValuePersister = new PropertyValuePersister(elementService);

    getSupportedComponentTypes(): string[] {
        return ["DatePickerField"];
    }

    getInitialValues(component: UiComponent, dataContext: UiComponentDataContext, renderingContext: UiComponentRenderingContext): Promise<Record<string, any>> {
        const attributeDefinitionId = component.attributeDefinitionId;
        const value = this.getPropertyValueAsDate(dataContext.element, dataContext.attributeDefinitions[component.attributeDefinitionId]);
        if (value) {
            return Promise.resolve(Object.fromEntries([[attributeDefinitionId, value]]));
        } else {
            return Promise.resolve({});
        }
    }

    private getPropertyValueAsDate(element: ElementDto, attributeDefinition?: AttributeDefinitionDto): Date | undefined {
        const value = this.getPropertyValue(element, attributeDefinition);
        return value ? new Date(value) : undefined;
    }

    private getPropertyValue(element: ElementDto, attributeDefinition?: AttributeDefinitionDto | undefined): string | undefined {
        const property = element.properties?.find(prop => prop.definition.name === attributeDefinition?.name);
        if (property) {
            return property.value;
        } else {
            return undefined;
        }
    }

    validate(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext, validationContext: UiComponentValidationContext): Record<string, string> {
        const datePickerField = component as DatePickerField;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[datePickerField.attributeDefinitionId];
        if (!attributeDefinition) {
            return {};
        }

        const value = stateContext.values[datePickerField.attributeDefinitionId];
        return validationContext.valueValidator.validateValue(value, attributeDefinition, t);
    }

    renderComponent({component, dataContext, stateContext, renderingContext}: UiComponentRenderingProps): ReactElement {
        const datePickerField = component as DatePickerField;

        const attributeDefinition = dataContext.attributeDefinitions[datePickerField.attributeDefinitionId];
        if (!attributeDefinition) {
            return (
                    <ErrorFieldPlaceholder key={datePickerField.id} error={t(StereotypesTranslationKey.UNKNOWN_ATTRIBUTE)} />
            );
        }

        const value = stateContext.values[datePickerField.attributeDefinitionId];
        const error = stateContext.errors[datePickerField.attributeDefinitionId];
        const readOnly = datePickerField.readOnly || !stateContext.editMode;

        return (
                <DatePicker
                        key={datePickerField.id}
                        label={attributeDefinition.name}
                        value={value}
                        onChange={(value) => stateContext.onValuesChange({[datePickerField.attributeDefinitionId]: value})}
                        readOnly={readOnly}
                        required={attributeDefinition.required}
                        error={error}
                        helperText={datePickerField.helperText}/>
        );
    }

    save(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext): Promise<void>[] {
        const datePickerField = component as DatePickerField;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[datePickerField.attributeDefinitionId];
        if (!attributeDefinition) {
            return [];
        }

        const value = stateContext.values[datePickerField.attributeDefinitionId];
        return [this.valuePersister.updateValue(dataContext.element, attributeDefinition, value)];
    }

}
